import { Button } from '@ibsys/kit';
import { DragDropProvider, type DragEndEvent, PointerSensor } from '@dnd-kit/react';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { isSortable } from '@dnd-kit/react/sortable';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { PlusIcon } from 'lucide-react';
import { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { RuleRow } from './RuleRow';
import { type BlockCardFormValues } from './types';

type RuleListProps = {
  isLeafBlock: boolean;
  isUpdating: boolean;
};

export const RuleList: FC<RuleListProps> = ({ isLeafBlock, isUpdating }) => {
  const { t } = useTranslation('expertReport');
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const { control } = useFormContext<BlockCardFormValues>();
  const { fields, append, remove, move } = useFieldArray({ control, name: 'rules' });

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.canceled) return;
    const { source } = event.operation;
    if (!isSortable(source)) return;
    const { initialIndex, index } = source;
    if (initialIndex !== index) {
      move(initialIndex, index);
    }
  };

  const handleAdd = () => {
    append({ name: '', text: '', predicate: undefined, knowledgeSets: [] });
  };

  if (!isLeafBlock) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">{t('rules')}</div>
      {fields.length > 0 ? (
        <DragDropProvider
          sensors={[PointerSensor.configure({ activationConstraints: [new PointerActivationConstraints.Distance({ value: 5 })] })]}
          modifiers={[RestrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <div className="space-y-2">
            {fields.map((field, index) => (
              <RuleRow
                key={field.id}
                index={index}
                sortableIndex={index}
                sortableId={field.id}
                onRemove={() => remove(index)}
                isUpdating={isUpdating}
              />
            ))}
          </div>
        </DragDropProvider>
      ) : (
        <div className="text-sm text-muted-foreground">{t('noRules')}</div>
      )}
      {allowAdmin && (
        <Button variant="outline" size="sm" icon={<PlusIcon className="size-4" />} onClick={handleAdd}>
          {t('addRule')}
        </Button>
      )}
    </div>
  );
};
